/**
 * Phase 10 - /check-in staff console.
 *
 * Modes: QR Scanner (html5-qrcode) + Manual Search.
 * Restricted-view payload (no PII beyond preferred name + primary photo).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, Accessibility, ScanLine, Search, RotateCcw } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

// html5-qrcode is dynamically imported to keep the initial bundle small
const CheckInPage = () => {
  const { user } = useAuth();
  const params = useParams();
  // Support /admin/events/:eventId/check-in route as well as bare /check-in.
  const routeEventId = params?.eventId || null;
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('scanner');
  const [result, setResult] = useState(null); // safe payload or {error, reason}
  const [pendingDuplicate, setPendingDuplicate] = useState(null); // {payload, prev}
  const [busy, setBusy] = useState(false);

  // Bundle 175 - Task 3. Prior behavior defaulted to
  // `data.events[0]` from the public `/events` list which is NOT
  // filtered by `end_datetime`, so a stale published event could
  // pin the console to a past event (Philly repro: admins scanned
  // Dallas tickets by accident). Load only active + upcoming events
  // from the Bundle 175 admin picker endpoint. If exactly one is
  // active, auto-select. If zero, render a friendly empty state.
  const [activeEvents, setActiveEvents] = useState(null); // null while loading
  useEffect(() => {
    (async () => {
      try {
        if (routeEventId) {
          const { data } = await api.get(`/events/${routeEventId}`);
          setEvent(data);
          setActiveEvents([]);
          return;
        }
        const { data } = await api.get('/admin/events-active-upcoming');
        const evs = data?.events || [];
        setActiveEvents(evs);
        if (evs.length === 1) {
          setEvent(evs[0]);
        }
      } catch { setActiveEvents([]); }
    })();
  }, [routeEventId]);

  // Poll stats every 15s
  useEffect(() => {
    if (!event?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/check-in/event/${event.id}/status`);
        if (!cancelled) setStats(data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [event?.id]);

  const submitVerify = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post('/check-in/verify', payload);
      if (data.is_duplicate && !pendingDuplicate) {
        setPendingDuplicate({ payload, data });
      } else {
        setResult({ ok: true, ...data });
        setPendingDuplicate(null);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Verification failed';
      setResult({ ok: false, reason: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally { setBusy(false); }
  };

  const clearResult = () => { setResult(null); setPendingDuplicate(null); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white">Check-in</h1>
            <div className="text-primary-200 text-sm">{event?.title || 'Loading event...'}</div>
          </div>
          <div className="text-right text-xs">
            <div className="text-primary-200">Signed in as</div>
            <div className="text-secondary-300 font-semibold">{user?.email}</div>
          </div>
        </div>

        {/* Bundle 175 - Task 3: event picker when none is auto-selected */}
        {!event && activeEvents !== null ? (
          <div
            className="rounded-lg border border-primary-700 bg-primary-900/60 p-5 space-y-3"
            data-testid="check-in-event-picker"
          >
            <h2 className="text-lg font-semibold text-white">
              Choose an event to scan
            </h2>
            {activeEvents.length === 0 ? (
              <div className="text-primary-200 text-sm">
                No active or upcoming events. Head back to the{' '}
                <a
                  href="/admin/events"
                  className="underline text-secondary-300"
                >events list</a>.
              </div>
            ) : (
              <div className="space-y-2">
                {activeEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => setEvent(ev)}
                    className="w-full text-left rounded-md border border-primary-600 bg-primary-800 hover:bg-primary-700 p-3"
                    data-testid={`check-in-pick-${ev.id}`}
                  >
                    <div className="font-medium text-white">{ev.title}</div>
                    <div className="text-xs text-primary-200">
                      {ev.city} - starts {new Date(ev.start_datetime).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="check-in-stats">
          <StatBox label="Confirmed" value={stats?.total_confirmed ?? '-'} />
          <StatBox label="Checked-in" value={stats?.checked_in ?? '-'} accent />
          <StatBox label="Remaining" value={stats?.remaining ?? '-'} />
        </div>

        {/* Result banner (fills the page if active) */}
        {(result || pendingDuplicate) && (
          <ResultCard
            result={result}
            pending={pendingDuplicate}
            onDone={clearResult}
            onConfirmDuplicate={() => submitVerify(pendingDuplicate.payload)}
          />
        )}

        {/* Modes */}
        {!result && !pendingDuplicate && (
          <>
            <div className="flex gap-2 mb-4">
              <button
                className={`flex-1 py-3 rounded-xl border-2 font-semibold transition-all ${mode === 'scanner' ? 'border-secondary-500 bg-secondary-500/10 text-secondary-200' : 'border-white/10 text-primary-200 hover:text-white hover:border-white/25'}`}
                onClick={() => setMode('scanner')}
                data-testid="check-in-tab-scanner"
              >
                <ScanLine className="h-4 w-4 inline-block mr-2" /> QR Scanner
              </button>
              <button
                className={`flex-1 py-3 rounded-xl border-2 font-semibold transition-all ${mode === 'manual' ? 'border-secondary-500 bg-secondary-500/10 text-secondary-200' : 'border-white/10 text-primary-200 hover:text-white hover:border-white/25'}`}
                onClick={() => setMode('manual')}
                data-testid="check-in-tab-manual"
              >
                <Search className="h-4 w-4 inline-block mr-2" /> Manual Search
              </button>
            </div>

            {mode === 'scanner' ? (
              <ScannerMode busy={busy} onScan={(tok) => submitVerify({ signed_token: tok })} />
            ) : (
              <ManualSearchMode eventId={event?.id} busy={busy}
                                onSelect={(regId) => submitVerify({ registration_id: regId })} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div className={`rounded-xl p-3 border ${accent ? 'border-secondary-500/40 bg-secondary-500/10' : 'border-white/10 bg-white/5'}`}>
    <div className="text-[10px] uppercase tracking-widest text-primary-200">{label}</div>
    <div className={`text-2xl font-bold ${accent ? 'text-secondary-300' : 'text-white'}`}>{value}</div>
  </div>
);

const ScannerMode = ({ busy, onScan }) => {
  const containerId = 'html5-qr-region';
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ text: null, at: 0 });
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const mod = await import('html5-qrcode');
        const { Html5Qrcode } = mod;
        const inst = new Html5Qrcode(containerId);
        scannerRef.current = inst;
        const config = { fps: 10, qrbox: { width: 240, height: 240 } };
        await inst.start({ facingMode: 'environment' }, config,
          (decodedText) => {
            const now = Date.now();
            if (lastScanRef.current.text === decodedText && (now - lastScanRef.current.at) < 2500) return;
            lastScanRef.current = { text: decodedText, at: now };
            // Extract signed token from URL: {base}/check-in/verify?t={token}
            let token = decodedText;
            try {
              const u = new URL(decodedText);
              token = u.searchParams.get('t') || decodedText;
            } catch { /* raw text OK */ }
            onScan(token);
          },
          () => { /* per-frame error - ignore */ },
        );
        if (!stopped) setStarting(false);
      } catch (e) {
        setError(String(e?.message || e));
        setStarting(false);
      }
    })();
    return () => {
      stopped = true;
      const inst = scannerRef.current;
      if (inst) {
        (async () => {
          try { await inst.stop(); } catch (_) { /* not running */ }
          try { inst.clear && inst.clear(); } catch (_) { /* ignore */ }
        })();
      }
    };
  }, []); // eslint-disable-line

  return (
    <Card className="bg-white/5 border-white/10 text-white overflow-hidden">
      <CardContent className="p-4 space-y-3">
        <div id={containerId} className="rounded-lg overflow-hidden bg-black min-h-[280px]" data-testid="qr-scanner-region" />
        {starting && !error && (
          <div className="text-center text-primary-200 text-sm">Requesting camera access...</div>
        )}
        {error && (
          <div className="rounded-lg border border-accent-400/50 bg-accent-500/10 text-accent-200 p-3 text-sm">
            Camera unavailable: {error}. Use Manual Search instead, or paste a token below.
          </div>
        )}
        <ManualTokenFallback busy={busy} onSubmit={(tok) => onScan(tok)} />
      </CardContent>
    </Card>
  );
};

const ManualTokenFallback = ({ busy, onSubmit }) => {
  const [tok, setTok] = useState('');
  return (
    <div className="pt-2 border-t border-white/10 mt-2">
      <div className="text-[10px] uppercase tracking-widest text-primary-200 mb-1">Paste a QR token or URL</div>
      <div className="flex gap-2">
        <Input
          value={tok} onChange={(e) => setTok(e.target.value)}
          placeholder="eyJhbGciOi... or full URL"
          className="bg-white/10 border-white/20 text-white placeholder:text-primary-200/60"
          data-testid="manual-token-input"
        />
        <Button
          onClick={() => { let t = tok; try { const u = new URL(tok); t = u.searchParams.get('t') || tok; } catch {} onSubmit(t); }}
          disabled={busy || !tok}
          className="bg-secondary-500 text-primary-900 hover:bg-secondary-400"
          data-testid="manual-token-submit-btn"
        >
          Verify
        </Button>
      </div>
    </div>
  );
};

const ManualSearchMode = ({ eventId, busy, onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!eventId || !q.trim()) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/check-in/search', { params: { q, event_id: eventId } });
        setResults(data.results || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, eventId]);

  return (
    <Card className="bg-white/5 border-white/10 text-white">
      <CardContent className="p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-200" />
          <Input
            ref={inputRef}
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search preferred name, phone, or ticket code..."
            className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-primary-200/60"
            data-testid="manual-search-input"
          />
        </div>
        {searching && <div className="text-xs text-primary-200">Searching...</div>}
        <div className="divide-y divide-white/10">
          {results.map((r) => (
            <div key={r.registration_id} className="py-2 flex items-center gap-3" data-testid={`manual-search-row-${r.registration_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{r.preferred_name}</div>
                <div className="text-xs text-primary-200 font-mono">{r.ticket_code}</div>
              </div>
              {r.is_duplicate ? (
                <Badge variant="outline" className="border-secondary-500/50 text-secondary-300 bg-secondary-500/10">Already in</Badge>
              ) : (
                <Button size="sm" disabled={busy} onClick={() => onSelect(r.registration_id)}
                        className="bg-secondary-500 text-primary-900 hover:bg-secondary-400">
                  Check in
                </Button>
              )}
            </div>
          ))}
          {q && !searching && results.length === 0 && (
            <div className="py-4 text-center text-primary-200 text-sm">No matches. Try phone or ticket code.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ResultCard = ({ result, pending, onDone, onConfirmDuplicate }) => {
  if (pending) {
    const prev = pending.data.previous_check_in;
    return (
      <Card className="border-secondary-500/40 bg-secondary-500/10 text-white shadow-lg mb-4" data-testid="check-in-duplicate-banner">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2 text-secondary-300 font-semibold">
            <AlertTriangle className="h-5 w-5" /> Already checked in
          </div>
          <div className="text-white font-display text-2xl">{pending.data.preferred_name}</div>
          {prev?.at && (
            <div className="text-sm text-primary-200">
              At {new Date(prev.at).toLocaleTimeString()} {prev.by ? `by ${prev.by}` : ''}. Check in anyway?
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onDone} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
            <Button onClick={onConfirmDuplicate} className="bg-secondary-500 text-primary-900 hover:bg-secondary-400" data-testid="check-in-duplicate-confirm-btn">
              Confirm anyway
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  if (result?.ok) {
    return (
      <Card className="border-success-500/40 bg-success-500/10 text-white shadow-lg mb-4" data-testid="checkin-scanner-result-success">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-10 w-10 text-success-400" />
            <div>
              <div className="text-xs uppercase tracking-widest text-primary-200">Checked in</div>
              <div className="font-display text-3xl md:text-4xl font-bold text-white" data-testid="check-in-guest-name">
                {result.preferred_name}
              </div>
            </div>
          </div>
          {result.primary_photo_thumb_url && (
            <img
              src={result.primary_photo_thumb_url}
              alt=""
              className="w-24 h-24 rounded-full object-cover ring-2 ring-secondary-500/60"
              data-testid="checkin-scanner-guest-photo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-primary-200">Ticket</div>
              <div className="font-mono font-bold text-secondary-300">{result.ticket_code}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-primary-200">Table</div>
              <div className="font-semibold text-white">{result.table_assignment || 'TBD'}</div>
            </div>
          </div>
          {result.food_allergies && (
            <div className="rounded-lg bg-accent-500/20 border border-accent-400/50 p-3 flex items-start gap-2" data-testid="check-in-allergy-alert">
              <AlertTriangle className="h-5 w-5 text-accent-300 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-accent-300">Allergy alert</div>
                <div className="text-white font-semibold">{result.food_allergies}</div>
              </div>
            </div>
          )}
          {result.accessibility_request && (
            <div className="rounded-lg bg-blue-500/20 border border-blue-400/50 p-3 flex items-start gap-2" data-testid="check-in-access-alert">
              <Accessibility className="h-5 w-5 text-blue-300 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-blue-300">Accessibility</div>
                <div className="text-white">{result.accessibility_request}</div>
              </div>
            </div>
          )}
          <Button onClick={onDone} className="w-full bg-secondary-500 text-primary-900 hover:bg-secondary-400 font-semibold" data-testid="checkin-scanner-continue">
            Next guest <RotateCcw className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-accent-500/40 bg-accent-500/10 text-white shadow-lg mb-4" data-testid="checkin-scanner-result-failure">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-accent-300 font-semibold">
          <XCircle className="h-5 w-5" /> Check-in failed
        </div>
        <div className="text-white">{result?.reason || 'Unknown error'}</div>
        <Button onClick={onDone} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="checkin-scanner-try-again">Try again</Button>
      </CardContent>
    </Card>
  );
};

export default CheckInPage;
